import { Dialog } from "@headlessui/react"
import React, { FC, useEffect, useState } from "react"
import { X } from "heroicons-react"
import { toast } from "react-toastify"
import { KButton, KInput, KSpan, KTextArea, KTitleSpan } from "kahuna-base-react-components"
import { marketingMilestoneEvents } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { ArtistPushPreview, MarketingMilestoneEvent } from "../../../../types"
import Loading from "../../../ui/Loading"

type Language = "tr" | "en" | "de"

const LANGUAGE_OPTIONS: { label: string; value: Language }[] = [
  { label: "TR", value: "tr" },
  { label: "EN", value: "en" },
  { label: "DE", value: "de" }
]

type Props = {
  open: boolean
  onClose: () => void
  event: MarketingMilestoneEvent | null
  onSent?: (eventId: number, sentAt: string) => void
}

const SendArtistPushModal: FC<Props> = ({ open, onClose, event, onSent }) => {
  const [language, setLanguage] = useState<Language>("tr")
  const [title, setTitle] = useState<string>("Kahuna")
  const [body, setBody] = useState<string>("")
  const [preview, setPreview] = useState<ArtistPushPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open || !event) return
    setLoading(true)
    setLanguage("tr")
    setTitle("Kahuna")
    setBody("")
    setPreview(null)

    marketingMilestoneEvents(`/${event.id}/push_preview/`, "get")
      .then((resp) => {
        const data = resp.data
        const next: ArtistPushPreview = {
          title: data.title || "Kahuna",
          bodies: data.bodies || { tr: "", en: "", de: "" },
          targetUserIds: data.target_user_ids || [],
          alreadySentAt: data.already_sent_at || null
        }
        setPreview(next)
        setTitle(next.title)
        setBody(next.bodies.tr || "")
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.detail || "Error loading push preview", errorFilledToastOptions)
      })
  }, [open, event])

  const onLanguageChange = (next: Language) => {
    setLanguage(next)
    if (preview?.bodies) setBody(preview.bodies[next] || "")
  }

  const send = () => {
    if (!event) return
    if (!body.trim()) {
      toast.error("Body cannot be empty", errorFilledToastOptions)
      return
    }
    setSending(true)
    marketingMilestoneEvents(`/${event.id}/send_artist_notification/`, "post", {
      language,
      title,
      body
    })
      .then((resp) => {
        setSending(false)
        const userCount = resp.data.user_count ?? 0
        const pushCount = resp.data.push_sent_count ?? resp.data.sent_count ?? 0
        toast.success(
          `Notified ${userCount} artist(s) on web · ${pushCount} mobile push${pushCount === 1 ? "" : "es"} delivered`,
          successFilledToastOptions
        )
        if (onSent) onSent(event.id, new Date().toISOString())
        onClose()
      })
      .catch((err) => {
        setSending(false)
        toast.error(err?.response?.data?.detail || "Error sending push", errorFilledToastOptions)
      })
  }

  if (!event) return null

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-2xl w-[520px] max-w-full p-6 relative">
          <button className="absolute top-4 right-4" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>

          {(loading || sending) && <Loading />}

          <KTitleSpan
            text="Send Notification"
            fontSize={20}
            color="#111"
            lineHeight="28px"
            fontWeight={600}
          />
          <div className="mt-1 mb-4">
            <KSpan
              text={
                event.artistName
                  ? `To: ${event.artistName}${event.trackTitle ? ` · ${event.trackTitle}` : ""}`
                  : event.trackTitle || event.title
              }
              fontSize={13}
              color="#666"
            />
          </div>

          {preview?.alreadySentAt && (
            <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Already sent at {new Date(preview.alreadySentAt).toLocaleString()} — sending again will deliver another notification.
            </div>
          )}

          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Language</p>
            <div className="flex border border-gray-200 rounded-lg overflow-hidden w-fit">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`h-9 px-4 text-sm ${
                    language === option.value ? "bg-[#111] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  onClick={() => onLanguageChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Title</p>
            <KInput value={title} placeholder="Title" onChange={setTitle} height="40px" />
          </div>

          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Body</p>
            <KTextArea value={body} placeholder="Notification body" onChange={setBody} height="120px" />
          </div>

          <div className="mb-4 text-xs text-gray-500">
            Target users: {preview?.targetUserIds?.length ?? 0}
          </div>

          <div className="flex justify-end gap-2">
            <KButton
              text="Cancel"
              height="36px"
              width="100px"
              background="#FFF"
              hoverBackground="#F5F5F5"
              onClick={onClose}
            />
            <KButton
              text="Send"
              height="36px"
              width="120px"
              background="#111"
              hoverBackground="#333"
              textColor="#FFF"
              onClick={send}
              disabled={sending || loading || !body.trim() || (preview?.targetUserIds?.length ?? 0) === 0}
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default SendArtistPushModal
